import React, { useMemo, useState } from 'react';

import {
	Alert,
	AlertIcon,
	Badge,
	Box,
	Button,
	Flex,
	Heading,
	HStack,
	Icon,
	Input,
	Select,
	SimpleGrid,
	Spinner,
	Table,
	Tbody,
	Td,
	Text,
	Th,
	Thead,
	Tr,
	VStack,
} from '@chakra-ui/react';

import {
	FiArrowLeft,
	FiFileText,
	FiPlus,
	FiRefreshCw,
	FiSearch,
} from 'react-icons/fi';

import {
	Link as RouterLink,
	useParams,
} from 'react-router-dom';

import { toast } from 'react-toastify';

import Card from 'components/card/Card';

import {
	useListSettingsChannelsQuery,
	useListMetaWhatsappTemplatesQuery,
	useSyncTemplatesMutation,
} from 'api/metaWhatsappApi';

import {
	getTemplateLanguageLabel,
	getTemplateQualityColorScheme,
	getTemplateStatusColorScheme,
	templateCategories,
	templateStatuses,
} from './helpers';
import { usePermissions } from 'hooks/usePermissions';

const ASSIGNMENTS_ROUTE =
	'/whatsapp/settings?tab=assignments';

const templateRoute = (channelId) =>
	`/whatsapp/settings/meta/templates/${encodeURIComponent(
		channelId
	)}`;

const formatDateTime = (value) => {
	if (!value) return '—';

	const date = new Date(value);

	if (Number.isNaN(date.getTime())) {
		return '—';
	}

	return new Intl.DateTimeFormat(undefined, {
		dateStyle: 'medium',
		timeStyle: 'short',
	}).format(date);
};

function StatusBadge({ status }) {
	return (
		<Badge
	colorScheme={getTemplateStatusColorScheme(status)}
	variant="subtle"
	borderRadius="full"
	px="2.5"
	py="1"
>
	{status || 'UNKNOWN'}
</Badge>
	);
}

function TemplateMobileCard({ template }) {
	return (
		<Card
			bg="bg.surface"
			borderWidth="1px"
			borderColor="border.default"
		>
			<VStack align="stretch" spacing="3">
				<Flex
					justify="space-between"
					gap="3"
					align="start"
				>
					<Box minW="0">
						<Text
							fontWeight="600"
							color="text.primary"
							wordBreak="break-word"
						>
							{template.name}
						</Text>

						<Text
							fontSize="xs"
							color="text.muted"
							mt="1"
						>
							{getTemplateLanguageLabel(template.language)}
						</Text>
					</Box>

					<StatusBadge status={template.status} />
				</Flex>

				<HStack spacing="2" flexWrap="wrap">
					<Badge variant="subtle">
						{template.category || 'Unknown'}
					</Badge>

					{template.qualityScore && (
					<Badge
	colorScheme={getTemplateQualityColorScheme(
		template.qualityScore
	)}
	variant="subtle"
>
	{template.qualityScore || 'UNKNOWN'}
</Badge>
					)}
				</HStack>

				<Text fontSize="xs" color="text.muted">
					Last synced:{' '}
					{formatDateTime(template.lastSyncedAt)}
				</Text>

				{template.rejectedReason && (
					<Alert
						status="error"
						borderRadius="md"
						fontSize="xs"
					>
						<AlertIcon />
						{template.rejectedReason}
					</Alert>
				)}
			</VStack>
		</Card>
	);
}

export default function MetaWhatsappTemplates() {
	const { channelId } = useParams();

	const [search, setSearch] = useState('');
	const [status, setStatus] = useState('');
	const [category, setCategory] = useState('');

		const { hasPermission } = usePermissions();
		const canTempalteCreate = hasPermission('whatsapp', 'template.create');

	const {
		data: channelsResponse,
		isLoading: channelsLoading,
		isError: channelsError,
	} = useListSettingsChannelsQuery();



	const {
	data: templates = [],
	isLoading: templatesLoading,
	isFetching: templatesFetching,
	isError: templatesError,
	refetch: refetchTemplates,
} = useListMetaWhatsappTemplatesQuery(
	{
		channelId,
	},
	{
		skip: !channelId,
		refetchOnMountOrArgChange: true,
	}
);
	const [
		syncTemplates,
		{ isLoading: syncing },
	] = useSyncTemplatesMutation();

	const channels =
		channelsResponse?.channels ?? [];

	const channel = useMemo(
		() =>
			channels.find(
				(item) =>
					String(item.channelId) ===
					String(channelId)
			),
		[channels, channelId]
	);

const wabaTemplates = useMemo(
	() =>
		[...templates].sort((a, b) =>
			String(a.name ?? '').localeCompare(
				String(b.name ?? '')
			)
		),
	[templates]
);

	const filteredTemplates = useMemo(() => {
		const needle = search.trim().toLowerCase();

		return wabaTemplates.filter((template) => {
			const matchesSearch =
				!needle ||
				String(template.name ?? '')
					.toLowerCase()
					.includes(needle) ||
				String(template.language ?? '')
					.toLowerCase()
					.includes(needle);

			const matchesStatus =
				!status ||
				String(template.status ?? '').toUpperCase() ===
					status;

			const matchesCategory =
				!category ||
				String(
					template.category ?? ''
				).toUpperCase() === category;

			return (
				matchesSearch &&
				matchesStatus &&
				matchesCategory
			);
		});
	}, [
		wabaTemplates,
		search,
		status,
		category,
	]);

	const approvedCount = wabaTemplates.filter(
		(template) =>
			template.status === 'APPROVED'
	).length;

	const pendingCount = wabaTemplates.filter(
		(template) =>
			template.status === 'PENDING'
	).length;

	const rejectedCount = wabaTemplates.filter(
		(template) =>
			template.status === 'REJECTED'
	).length;

	const handleRefresh = async () => {
		try {
			const result =
				await syncTemplates().unwrap();

			await refetchTemplates();

			if (result?.errors?.length) {
				toast.warning(
					`Templates refreshed with ${result.errors.length} account warning(s).`
				);
			} else {
				toast.success(
					'Templates refreshed from Meta.'
				);
			}
		} catch (error) {
			toast.error(
				error?.data?.message ||
					error?.data?.error ||
					'Could not refresh templates from Meta.'
			);
		}
	};

	if (channelsLoading || templatesLoading) {
		return (
			<Flex
				minH="320px"
				align="center"
				justify="center"
			>
				<Spinner size="md" />
			</Flex>
		);
	}

	if (channelsError || templatesError) {
		return (
			<Alert status="error" borderRadius="lg">
				<AlertIcon />
				WhatsApp templates could not be loaded.
			</Alert>
		);
	}

	if (!channel) {
		return (
			<Card
				bg="bg.surface"
				borderWidth="1px"
				borderColor="border.default"
			>
				<VStack align="start" spacing="3">
					<Heading size="sm">
						WhatsApp number not found
					</Heading>

					<Text
						fontSize="sm"
						color="text.muted"
					>
						This number is no longer available in
						the tenant&apos;s Meta WhatsApp
						configuration.
					</Text>

					<Button
						as={RouterLink}
						to={ASSIGNMENTS_ROUTE}
						size="sm"
						leftIcon={
							<Icon as={FiArrowLeft} />
						}
					>
						Back to assignments
					</Button>
				</VStack>
			</Card>
		);
	}

	/*
	 * Never show every tenant template when WABA identity is
	 * unavailable. That could mix catalogues from different
	 * Meta accounts.
	 */
	// if (!channel.wabaId) {
	// 	return (
	// 		<Alert status="warning" borderRadius="lg">
	// 			<AlertIcon />
	// 			This channel does not expose its WABA ID, so its
	// 			template catalogue cannot be safely scoped.
	// 		</Alert>
	// 	);
	// }

	const displayNumber =
		channel.label ||
		channel.displayPhoneNumber ||
		channel.phoneNumberId;

	return (
		<VStack
			align="stretch"
			spacing="5"
			maxW="1400px"
			mx="auto"
			w="full"
		>
			<Flex
				justify="space-between"
				align={{
					base: 'stretch',
					md: 'center',
				}}
				direction={{
					base: 'column',
					md: 'row',
				}}
				gap="4"
			>
				<Box>
					<Button
						as={RouterLink}
						to={ASSIGNMENTS_ROUTE}
						variant="ghost"
						size="sm"
						leftIcon={
							<Icon as={FiArrowLeft} />
						}
						mb="2"
					>
						Assignments
					</Button>

					<HStack spacing="3">
						<Flex
							w="10"
							h="10"
							borderRadius="lg"
							bg="bg.subtle"
							align="center"
							justify="center"
							flexShrink="0"
						>
							<Icon
								as={FiFileText}
								boxSize="5"
								color="text.accent"
							/>
						</Flex>

						<Box>
							<Heading
								size="md"
								color="text.primary"
							>
								Message templates
							</Heading>

							<Text
								fontSize="sm"
								color="text.muted"
							>
								{displayNumber}
							</Text>
						</Box>
					</HStack>
				</Box>

				<HStack
					spacing="2"
					flexWrap="wrap"
				>
					<Button
						size="sm"
						variant="outline"
						leftIcon={
							<Icon as={FiRefreshCw} />
						}
						onClick={handleRefresh}
						isLoading={syncing}
						isDisabled={
							syncing ||
							templatesFetching
						}
					>
						Refresh from Meta
					</Button>

{canTempalteCreate && 

					<Button
						as={RouterLink}
						to={`${templateRoute(
							channelId
						)}/create`}
						size="sm"
						colorScheme="brand"
						leftIcon={
							<Icon as={FiPlus} />
						}
					>
						Create template
					</Button>
}

				</HStack>
			</Flex>

			<Text fontSize="sm" color="text.muted">
				Templates belong to the WhatsApp Business
				Account. Numbers connected to the same WABA
				share this catalogue.
			</Text>

			<SimpleGrid
				columns={{
					base: 2,
					md: 4,
				}}
				spacing="3"
			>
				{[
					[
						'Total',
						wabaTemplates.length,
						'gray',
					],
					[
						'Approved',
						approvedCount,
						'green',
					],
					[
						'Pending',
						pendingCount,
						'yellow',
					],
					[
						'Rejected',
						rejectedCount,
						'red',
					],
				].map(([label, value, color]) => (
					<Card
						key={label}
						bg="bg.surface"
						borderWidth="1px"
						borderColor="border.default"
					>
						<Text
							fontSize="xs"
							color="text.muted"
						>
							{label}
						</Text>

						<Text
							fontSize="2xl"
							fontWeight="700"
							color={
								color === 'gray'
									? 'text.primary'
									: `${color}.500`
							}
						>
							{value}
						</Text>
					</Card>
				))}
			</SimpleGrid>

			<Card
				bg="bg.surface"
				borderWidth="1px"
				borderColor="border.default"
			>
				<Flex
					gap="3"
					direction={{
						base: 'column',
						md: 'row',
					}}
					mb="4"
				>
					<Box
						position="relative"
						flex="1"
					>
						<Icon
							as={FiSearch}
							position="absolute"
							left="3"
							top="50%"
							transform="translateY(-50%)"
							color="text.muted"
							pointerEvents="none"
						/>

						<Input
							value={search}
							onChange={(event) =>
								setSearch(
									event.target.value
								)
							}
							placeholder="Search templates"
							pl="9"
						/>
					</Box>

					<Select
						value={status}
						onChange={(event) =>
							setStatus(
								event.target.value
							)
						}
						maxW={{
							base: 'full',
							md: '190px',
						}}
					>
						<option value="">
							All statuses
						</option>
						<option value="APPROVED">
							Approved
						</option>
						<option value="PENDING">
							Pending
						</option>
						<option value="REJECTED">
							Rejected
						</option>
						<option value="PAUSED">
							Paused
						</option>
						<option value="DISABLED">
							Disabled
						</option>
					</Select>

					<Select
						value={category}
						onChange={(event) =>
							setCategory(
								event.target.value
							)
						}
						maxW={{
							base: 'full',
							md: '190px',
						}}
					>
						<option value="">
							All categories
						</option>
						<option value="MARKETING">
							Marketing
						</option>
						<option value="UTILITY">
							Utility
						</option>
						<option value="AUTHENTICATION">
							Authentication
						</option>
					</Select>
				</Flex>

				{wabaTemplates.length === 0 ? (
					<Flex
						minH="320px"
						align="center"
						justify="center"
						textAlign="center"
						px="4"
					>
						<VStack spacing="4">
							<Flex
								w="14"
								h="14"
								borderRadius="full"
								bg="bg.subtle"
								align="center"
								justify="center"
							>
								<Icon
									as={FiFileText}
									boxSize="6"
									color="text.muted"
								/>
							</Flex>

							<Box>
								<Heading size="sm">
									No templates yet
								</Heading>

								<Text
									mt="1"
									fontSize="sm"
									color="text.muted"
									maxW="420px"
								>
									Create your first
									WhatsApp template and
									submit it to Meta for
									review.
								</Text>
							</Box>

{canTempalteCreate &&
							<Button
								as={RouterLink}
								to={`${templateRoute(
									channelId
								)}/create`}
								colorScheme="brand"
								size="sm"
								leftIcon={
									<Icon
										as={
											FiPlus
										}
									/>
								}
							>
								Create template
							</Button>
}
						</VStack>
					</Flex>
				) : filteredTemplates.length === 0 ? (
					<Flex
						minH="240px"
						align="center"
						justify="center"
					>
						<Text
							fontSize="sm"
							color="text.muted"
						>
							No templates match these filters.
						</Text>
					</Flex>
				) : (
					<>
						<Box
							display={{
								base: 'none',
								md: 'block',
							}}
							overflowX="auto"
						>
							<Table size="sm">
								<Thead>
									<Tr>
										<Th>Name</Th>
										<Th>Language</Th>
										<Th>Category</Th>
										<Th>Status</Th>
										<Th>Quality</Th>
										<Th>
											Last synced
										</Th>
									</Tr>
								</Thead>

								<Tbody>
									{filteredTemplates.map(
										(template) => (
											<Tr
												key={`${template.wabaId}:${template.name}:${template.language}`}
											>
												<Td>
													<Text
														fontWeight="600"
														color="text.primary"
													>
														{
															template.name
														}
													</Text>

													{template.rejectedReason && (
														<Text
															fontSize="xs"
															color="danger"
															mt="1"
															maxW="320px"
														>
															{
																template.rejectedReason
															}
														</Text>
													)}
												</Td>

												<Td>
													{getTemplateLanguageLabel(
														template.language
													)}
												</Td>

												<Td>
													{
														template.category
													}
												</Td>

												<Td>
													<StatusBadge
														status={
															template.status
														}
													/>
												</Td>

												<Td>
													<Badge
														variant="subtle"
														colorScheme={getTemplateQualityColorScheme(
															template.qualityScore
														)}
													>
														{template.qualityScore ||
															'UNKNOWN'}
													</Badge>
												</Td>

												<Td
													whiteSpace="nowrap"
													fontSize="sm"
													color="text.muted"
												>
													{formatDateTime(
														template.lastSyncedAt
													)}
												</Td>
											</Tr>
										)
									)}
								</Tbody>
							</Table>
						</Box>

						<VStack
							display={{
								base: 'flex',
								md: 'none',
							}}
							align="stretch"
							spacing="3"
						>
							{filteredTemplates.map(
								(template) => (
									<TemplateMobileCard
										key={`${template.wabaId}:${template.name}:${template.language}`}
										template={
											template
										}
									/>
								)
							)}
						</VStack>
					</>
				)}
			</Card>
		</VStack>
	);
}